import React, { useContext, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import Issue, { IssueScope } from '../../../types/Issue';
import AnalyticsService from '../../../services/AnalyticsService/AnalyticsService';
import AsyncSearchSelect from '../../../components/AsyncSearchSelect/AsyncSearchSelect';
import CustomModal from '../../../components/CustomModal/CustomModal';
import CustomSelect from '../../../components/CustomSelect/CustomSelect';
import { Button } from '../../../components/ui/button';
import ChatbotContext from '../../../context/ChatbotContext/ChatbotContext';
import useDebouncedEffect from '../../../hooks/useDebouncedEffect';

interface IssueManagerProps {
  chatbotId: number;
  messageId: number;
  initialIssue?: Issue | null;
  onIssueChange?: (issue: Issue | null) => void;
  setError: React.Dispatch<React.SetStateAction<string>>;
}

const GENERIC_ERROR = 'Something went wrong. Please try again later.';
const MAX_SIMILAR_ISSUES = 20;

function IssueManager({
  chatbotId,
  messageId,
  initialIssue = null,
  onIssueChange,
  setError
}: Readonly<IssueManagerProps>): React.ReactElement {
  const { currentChatbot } = useContext(ChatbotContext);
  const scopeOptions = [
    { value: 'CHATBOT', label: currentChatbot?.name ?? 'This chatbot' },
    { value: 'GLOBAL', label: 'All chatbots (global)' }
  ];

  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(
    initialIssue
  );
  const [isCreating, setIsCreating] = useState(false);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newScope, setNewScope] = useState<IssueScope>('CHATBOT');
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [similarIssues, setSimilarIssues] = useState<Issue[]>([]);

  useEffect(() => {
    setSelectedIssue(initialIssue);
  }, [initialIssue]);

  const hasTitle = newTitle.trim() !== '';

  useDebouncedEffect(
    () => {
      if (!createModalOpen) return;
      const query = newTitle.trim();
      if (query.length < 2) {
        setSimilarIssues([]);
        return;
      }
      const needle = query.toLowerCase();
      AnalyticsService.searchIssues(chatbotId, query)
        .then((matches) => {
          const relevant = matches.filter(
            (issue) =>
              (issue.title ?? '').toLowerCase().includes(needle) ||
              (issue.description ?? '').toLowerCase().includes(needle)
          );
          setSimilarIssues(relevant.slice(0, MAX_SIMILAR_ISSUES));
        })
        .catch((err) => {
          if (err.name !== 'CanceledError') setSimilarIssues([]);
        });
    },
    [newTitle, createModalOpen, chatbotId],
    300
  );

  const reportError = (err: any): void => {
    if (err.name !== 'CanceledError') {
      setError(err.apiError?.message || GENERIC_ERROR);
    }
  };

  const attachIssue = async (issueId: number | null): Promise<void> => {
    setError('');
    const updated = await AnalyticsService.setMessageIssue(
      chatbotId,
      messageId,
      issueId
    );
    setSelectedIssue(updated);
    onIssueChange?.(updated);
  };

  const handleSelect = async (issue: Issue | null): Promise<void> => {
    try {
      await attachIssue(issue ? issue.id : null);
    } catch (err: any) {
      reportError(err);
    }
  };

  const openCreateModal = (): void => {
    setNewScope('CHATBOT');
    setNewTitle('');
    setNewDescription('');
    setSimilarIssues([]);
    setCreateModalOpen(true);
  };

  const closeCreateModal = (): void => {
    setCreateModalOpen(false);
    setSimilarIssues([]);
  };

  const handleAttachExisting = async (issue: Issue): Promise<void> => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      setError('');
      await attachIssue(issue.id);
      closeCreateModal();
    } catch (err: any) {
      reportError(err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (isCreating || !hasTitle) return;
    setIsCreating(true);
    try {
      setError('');
      const created = await AnalyticsService.createIssue(
        newScope,
        newScope === 'CHATBOT' ? chatbotId : null,
        newTitle.trim(),
        newDescription.trim()
      );
      await attachIssue(created.id);
      closeCreateModal();
    } catch (err: any) {
      reportError(err);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <AsyncSearchSelect<Issue>
        value={selectedIssue}
        onSelect={handleSelect}
        fetchOptions={(query) =>
          AnalyticsService.searchIssues(chatbotId, query)
        }
        getOptionKey={(issue) => issue.id}
        getTriggerLabel={(issue) =>
          issue.title ? `${issue.reference} · ${issue.title}` : issue.reference
        }
        placeholder='Attach an issue...'
        searchPlaceholder='Search issues...'
        emptyText='No issues found'
        clearable
        onError={(message) => setError(message)}
        renderOption={(issue) => (
          <div className='flex items-baseline gap-2'>
            <span className='shrink-0 font-medium'>{issue.reference}</span>
            {(issue.title || issue.description) && (
              <span className='text-text-secondary min-w-0 text-xs break-words'>
                {issue.title || issue.description}
              </span>
            )}
          </div>
        )}
        renderHeader={(close) => (
          <button
            type='button'
            className='text-brand-light flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium hover:bg-gray-50'
            onClick={() => {
              close();
              openCreateModal();
            }}
          >
            <Plus size={16} className='shrink-0' />
            New issue
          </button>
        )}
      />

      <CustomModal
        open={createModalOpen}
        onClose={closeCreateModal}
        title='New issue'
      >
        <div className='flex flex-col gap-4'>
          <div className='flex flex-col gap-1'>
            <span className='text-sm font-medium'>Scope</span>
            <CustomSelect
              value={newScope}
              onValueChange={(value) => setNewScope(value as IssueScope)}
              options={scopeOptions}
              className='h-9 text-sm'
            />
          </div>

          <div className='flex flex-col gap-1'>
            <span className='text-sm font-medium'>Title</span>
            <input
              type='text'
              maxLength={200}
              aria-label='Issue title'
              className='border-input bg-background rounded-md border p-2 text-sm'
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder='Short label for quick scanning'
            />
          </div>

          {similarIssues.length > 0 && (
            <div className='border-input flex flex-col gap-1.5 rounded-md border p-3'>
              <span className='text-text-secondary text-xs font-medium'>
                Similar existing issues. Attach one instead?
              </span>
              <div className='flex max-h-36 flex-col gap-1.5 overflow-y-auto'>
                {similarIssues.map((issue) => (
                  <button
                    key={issue.id}
                    type='button'
                    disabled={isCreating}
                    className='hover:bg-muted flex items-center gap-2 rounded px-1.5 py-1 text-left text-sm disabled:opacity-50'
                    onClick={() => handleAttachExisting(issue)}
                  >
                    <span className='font-medium'>{issue.reference}</span>
                    {(issue.title || issue.description) && (
                      <span className='text-text-secondary truncate text-xs'>
                        {issue.title || issue.description}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className='flex flex-col gap-1'>
            <span className='text-sm font-medium'>
              Description{' '}
              <span className='text-text-secondary font-normal'>
                (optional)
              </span>
            </span>
            <textarea
              maxLength={1000}
              aria-label='Issue description'
              className='border-input bg-background min-h-[80px] rounded-md border p-2 text-sm'
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder='Describe the issue'
            />
          </div>

          <div className='flex justify-end gap-2'>
            <Button
              variant='ghost'
              onClick={closeCreateModal}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isCreating || !hasTitle}>
              Create &amp; attach
            </Button>
          </div>
        </div>
      </CustomModal>
    </>
  );
}

export default IssueManager;
